import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet, HelmetTags } from 'react-helmet';
import { APP_DESCRIPTION, APP_TITLE } from '../../config/env';
import { GlobalConfigurationContext } from './GlobalConfigurationContext';
import useHistory from '../hook/useBrowserHistory';
import RequestConfigurationFactory from '../../Services/Request/RequestConfigurationFactory';

interface IContext {
  setDescription: (description: string) => void;
  setFavicon: (favicon: string) => void;
  setTitle: (title: string) => void;
}

export const HeadContext: React.Context<IContext> =
  React.createContext<IContext>({
    setDescription: () => {},
    setFavicon: () => {},
    setTitle: () => {},
  });

type Props = React.PropsWithChildren<unknown>;

const HeadContextProvider: React.FC<Props> = (props: Props) => {
  const { allowStandaloneCustomization } = useContext(
    GlobalConfigurationContext
  );

  const history = useHistory();

  const { children } = props;

  const [description, setDescription] = useState('');
  const [favicon, setFavicon] = useState('');
  const [title, setTitle] = useState('');

  const value = useMemo(
    () => ({ setDescription, setFavicon, setTitle }),
    [setDescription, setFavicon, setTitle]
  );

  const originalFavicon = useRef<HTMLLinkElement | null>(null);
  const originalTitle = useRef('');

  useEffect(() => {
    let active = true;

    originalTitle.current = document.title;

    const unlisten = history.listen(() => {
      if (!active) {
        return;
      }

      setDescription('');
      setFavicon('');
      setTitle('');
    });

    return () => {
      active = false;
      unlisten();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // @see https://github.com/nfl/react-helmet/issues/430
  const onHelmetChange = (_: any, addedTags: HelmetTags) => {
    try {
      // check if a favicon tag has been added programatically
      const addedFavicon = addedTags.linkTags?.find(
        (tag) => tag.href === favicon
      );

      if (!addedFavicon) {
        return;
      }

      // remove existing <link ref="icon" href="..."/> tags, which
      // represent the default favicon added from the index.html file
      document
        .querySelectorAll(`link[rel="icon"]:not([href="${favicon}"])`)
        .forEach((e) => {
          originalFavicon.current = e as HTMLLinkElement;
          e.parentNode?.removeChild(e);
        });

      // remove and add the favicon to let the browser refresh it
      const parent = addedFavicon.parentNode;
      parent?.removeChild(addedFavicon);
      parent?.appendChild(addedFavicon);
    } catch (e) {
      console.error(e);
    }
  };

  const renderDescription = (): JSX.Element | undefined => {
    // do not reset to the default description if no standalone customization is allowed
    if (!allowStandaloneCustomization) {
      return undefined;
    }

    return <meta name="description" content={description || APP_DESCRIPTION} />;
  };

  const renderFavicon = (): JSX.Element | undefined => {
    // don't overwrite to the default favicon if no standalone customization is allowed
    if (!(allowStandaloneCustomization || favicon)) {
      // restore the original favicon node if it was removed and the temporary
      // custom favicon has been removed (e.g. when closing the room config dialog)
      if (originalFavicon.current) {
        document.head.appendChild(originalFavicon.current);
        originalFavicon.current = null;
      }

      return;
    }

    const apiUrl = RequestConfigurationFactory.getApiUrl();
    const source = favicon || `${apiUrl}/favicon`;
    return <link rel="icon" href={source} />;
  };

  const renderTitle = (): JSX.Element | undefined => {
    // do not reset to the default title if no standalone customization is allowed
    if (!(allowStandaloneCustomization || title)) {
      return <title>{originalTitle.current}</title>;
    }

    return <title>{title || APP_TITLE}</title>;
  };

  return (
    <HeadContext.Provider value={value}>
      <Helmet onChangeClientState={onHelmetChange}>
        {renderFavicon()}
        {renderTitle()}
        {renderDescription()}
      </Helmet>
      {children}
    </HeadContext.Provider>
  );
};

export default HeadContextProvider;
